/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback } from "react";

type SidebarContextType = {
  isExpanded: boolean;
  toggleSidebar: () => void;
  setHovered: (v: boolean) => void;
  isMobileOpen: boolean;
  toggleMobileSidebar: () => void;
  closeSidebar: () => void;
  closeMobileSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SIDEBAR_WIDTH_EXPANDED_PX = 208;
export const SIDEBAR_WIDTH_COLLAPSED_PX = 64;

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsExpanded((s) => !s);
  }, []);

  const setHovered = useCallback((v: boolean) => {
    setIsHovered(v);
    if (v) {
      setIsExpanded(true);
    } else {
      setIsExpanded((prev) => prev && !isMobileOpen ? prev : prev);
    }
  }, [isMobileOpen]);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileOpen((s) => !s);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsExpanded(false);
    setIsMobileOpen(false);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isExpanded,
        toggleSidebar,
        setHovered,
        isMobileOpen,
        toggleMobileSidebar,
        closeSidebar,
        closeMobileSidebar,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
  return ctx;
};
